import dayjs from "dayjs";
import { getDecodedToken } from "../utils/tokenAccessor";

export const DATE_FILTER_OPTIONS = [
  {
    label: "Last 7 days",
    value: "LAST_7_DAYS",
  },
  {
    label: "Last 30 days",
    value: "LAST_30_DAYS",
  },
  {
    label: "Last 90 days",
    value: "LAST_90_DAYS",
  },
  {
    label: "Last 365 days",
    value: "LAST_365_DAYS",
  },
];

export const calculateDates = (selectedOption) => {
  const endDate = new Date();
  let startDate = new Date();

  // Calculate start date based on selected option
  switch (selectedOption) {
    case "LAST_7_DAYS":
      startDate.setDate(endDate.getDate() - 6); // Subtract 6 days to get 7 days ago
      break;
    case "LAST_30_DAYS":
      startDate.setDate(endDate.getDate() - 29); // Subtract 29 days to get 30 days ago
      break;
    case "LAST_90_DAYS":
      startDate.setDate(endDate.getDate() - 89); // Subtract 89 days to get 90 days ago
      break;
    case "LAST_365_DAYS":
      startDate.setDate(endDate.getDate() - 364); // Subtract 364 days to get 365 days ago
      break;
    default:
      break;
  }

  // Format dates to 'yyyy-mm-dd'
  const formattedStartDate = startDate.toISOString().split("T")[0];
  const formattedEndDate = endDate.toISOString().split("T")[0];

  return { startDate: formattedStartDate, endDate: formattedEndDate };
};

export const formatDate = (inputDate) => {
  if (!inputDate) return null;

  const date = new Date(inputDate);
  const d = date.getDate();
  const m = date.getMonth() + 1; //Month from 0 to 11
  const y = date.getFullYear();
  return `${y}-${m <= 9 ? "0" + m : m}-${d <= 9 ? "0" + d : d}`;
};

export const calculatePreviousDateRange = (
  currentStartDate,
  currentEndDate
) => {
  const startDate = new Date(currentStartDate);
  const endDate = new Date(currentEndDate);
  const prevEndDate = new Date(startDate.getTime() - 1); // Previous end date is one day before the current start date
  const prevStartDate = new Date(prevEndDate.getTime() - (endDate - startDate)); // Previous start date is the same duration before the previous end date as the current date range

  // Format dates to 'yyyy-MM-dd'
  const formattedPrevStartDate = prevStartDate.toISOString().split("T")[0];
  const formattedPrevEndDate = prevEndDate.toISOString().split("T")[0];

  return { startDate: formattedPrevStartDate, endDate: formattedPrevEndDate };
};

export const getRoundToTwoDecimals = (value, noOfDecimals = 2) => {
  if (Number.isNaN(Number(value))) return value;
  return (
    Math.round(value * Math.pow(10, noOfDecimals)) / Math.pow(10, noOfDecimals)
  );
};

export const deepClone = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (e) {
    return obj;
  }
};

export const getPowerBIPropertyFilter = (propertyCode) => ({
  $schema: "https://powerbi.com/product/schema#basic",
  target: {
    table: "dimProperty",
    column: "code",
  },
  operator: "In",
  values: [propertyCode],
  displaySettings: { isLockedInViewMode: true },
});

export const getPowerBIDateRangeFilter = (startDate, endDate) => {
  // Validate parameters
  if (!startDate || !endDate) {
    throw new TypeError(
      "Invalid date parameters: startDate and endDate are required"
    );
  }

  // Validate that dates are valid Date objects
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new TypeError("Invalid dates selected.");
  }

  return {
    $schema: "https://powerbi.com/product/schema#advanced",
    target: {
      table: "D_Arrival_Date",
      column: "Date",
    },
    logicalOperator: "And",
    conditions: [
      {
        operator: "GreaterThanOrEqual",
        value: start.toISOString(),
      },
      {
        operator: "LessThanOrEqual",
        value: end.toISOString(),
      },
    ],
    filterType: 0, // IAdvancedFilter
  };
};

export const formatNumberOfDays = (value) => {
  if (value === "N/A") {
    return value;
  }
  if (Number.isNaN(Number(value))) {
    return "Unlimited";
  }
  const dayLabel = value > 1 ? "days" : "day";
  return `${value} ${dayLabel}`;
};

export const safeParseJson = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return {};
  }
};
export const getDiscountDisplay = (percentDiscountMap) => {
  const daysOfWeek = [
    "Sun", // 1
    "Mon", // 2
    "Tue", // 3
    "Wed", // 4
    "Thu", // 5
    "Fri", // 6
    "Sat", // 7
  ];

  // Filter out non-zero values and map them to their day names
  const discounts = Object.entries(percentDiscountMap)
    .filter(([_, value]) => value !== 0) // Filter out zero values
    .map(([key, value]) => `${value}% (${daysOfWeek[key - 1]})`); // Map index to day name and format

  return discounts.join(", "); // Join the results into a single string
};

export const getDiscountValueFromRule = (rule, currency, dayOfWeek) => {
  if (!rule) return null;
  const { discountVO = {} } = rule.ruleVO || {};
  const {
    percentDiscountAmount,
    fixedDiscountAmount,
    fixedDiscountMap,
    percentDiscountMap,
    mCompDiscountType,
    discountType,
  } = discountVO;
  let discountAmount;
  if (discountType && discountType === 1) {
    discountAmount = getDiscountForType1(
      mCompDiscountType,
      fixedDiscountMap,
      percentDiscountMap,
      currency,
      dayOfWeek
    );
  } else if (discountType && discountType === 4) {
    if (mCompDiscountType && mCompDiscountType === 1) {
      discountAmount = formatCurrency(fixedDiscountAmount, currency);
    } else if (mCompDiscountType && mCompDiscountType === 2) {
      discountAmount = `${percentDiscountAmount}%`;
    }
  }

  return discountAmount;
};

export const getDiscountForType1 = (
  mCompDiscountType,
  fixedDiscountMap,
  percentDiscountMap,
  currency,
  dayOfWeek
) => {
  let discountAmount;
  if (mCompDiscountType && mCompDiscountType === 1) {
    if (fixedDiscountMap) {
      const fixedDiscountValues = Object.values(fixedDiscountMap);
      const totalFixedDiscountAmount = fixedDiscountValues.reduce(
        (previous, current) => current + previous
      );
      const amount = (
        totalFixedDiscountAmount / fixedDiscountValues.length
      ).toFixed(2);
      discountAmount = `${formatCurrency(amount, currency)}*`;
    }
  } else if (mCompDiscountType && mCompDiscountType === 2) {
    if (percentDiscountMap === null) {
      const percentDiscountValues = Object.values(percentDiscountMap);
      const totalPercentDiscountAmount = percentDiscountValues.reduce(
        (previous, current) => current + previous
      );
      discountAmount = `${(
        totalPercentDiscountAmount / percentDiscountValues.length
      ).toFixed(2)}%*`;
    } else {
      if (percentDiscountMap === null) {
        return undefined;
      }
      const percentDiscountValues = Object.values(percentDiscountMap);
      const percentDisMap = new Map();
      percentDiscountValues.forEach((value, index) => {
        if (value !== 0) {
          percentDisMap.set(index + 1, value);
        }
      });
      if (percentDisMap.has(dayOfWeek)) {
        const discountValue = percentDisMap.get(dayOfWeek);
        discountAmount = `${formatDiscountValue(discountValue)}%`;
      }
    }
  }
  return discountAmount;
};

export const getAppliesToRule = (rule) => {
  if (!rule) return {};
  const { ruleVO = {} } = rule;
  const { stayVO = {}, bookingVO = {}, discountVO } = ruleVO;
  const { discountTypeDescription } = discountVO || {};
  // get blackOutDateRanges from stayVO too
  const {
    stayStartDate,
    stayEndDate,
    minLengthOfStay,
    maxLengthOfStay,
    blackOutDateRanges = [],
  } = stayVO;
  const { bookingStartDate, bookingEndDate, minBookingLeadTime } = bookingVO;

  return {
    bookingStartDate,
    bookingEndDate,
    stayStartDate,
    stayEndDate,
    minLengthOfStay,
    maxLengthOfStay,
    minBookingLeadTime,
    discountTypeDescription,
    blackOutDateRanges,
  };
};

export function formatDateString(dateString) {
  const date = dayjs(dateString);
  const today = dayjs();
  const isToday = date.isSame(today, "day");
  const timeString = date.format("h.mm a");

  return isToday ? `Today, ${timeString}` : date.format("MMM D");
}

export const formatCurrency = (value, currency) => {
  if (Number.isNaN(Number(value)) || !currency) return value;

  // Create our number formatter.
  const formatter = new Intl.NumberFormat(
    navigator.language.includes("en") ? "en-US" : navigator.language,
    {
      style: "currency",
      currency,
      // trailingZeroDisplay: "stripIfInteger",
      // It will only stop printing
      // the fraction when the input
      // amount is a round number (int)
      // already.
    }
  );

  return formatter.format(value);
};

// Token-derived identity getters live in utils/tokenAccessor (single decoding
// implementation, quote-stripping included); re-exported here so existing
// consumers keep one import surface.
export {
  getLoggedInUserId,
  getLoggedInUserEmail,
} from "../utils/tokenAccessor";

// custom:userType JWT claim values (mirror of the backend UserType enum).
export const USER_TYPE = {
  SUPPLIER: "SUPPLIER",
  INTERNAL: "INTERNAL",
  CLIENT: "CLIENT",
};

// User type (and email, re-exported above) come from the ID token rather than
// PermissionContext.userInfo because userInfo arrives asynchronously: on a first
// login the provider hydrates from the Callback's sessionStorage cache without
// calling fetchUserInfo, and that cache carries no email — the token is present
// from the first render.
export const getLoggedInUserType = () =>
  getDecodedToken()?.["custom:userType"] ?? null;

// True only for supplier (hotel) users. Client users are view-only in the Action Centre
// and must not trigger state-changing card actions.
export const isSupplierUser = () =>
  getLoggedInUserType() === USER_TYPE.SUPPLIER;

// True for HotelTrader's own (AM/Supply) users. Kept separate from isSupplierUser rather
// than widening it: callers that mean "this is the hotel acting on its own account" still
// need that exact meaning, and only the card CTAs are opening up (EO-1268).
export const isInternalUser = () =>
  getLoggedInUserType() === USER_TYPE.INTERNAL;

export const formatDiscountValue = (value) => {
  if (Number.isInteger(value)) {
    return value; // Return as is if it's an integer
  }
  return value.toFixed(2); // Otherwise, format to 2 decimal places
};

export const normalizeToUTCMidnight = (date) => {
  const utcDate = new Date(date);
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate()
  );
};
